export type ProductHelpCategoryId =
  | 'start'
  | 'account-access'
  | 'commerce'
  | 'maps'
  | 'automation'
  | 'troubleshooting';

export type ProductHelpCategory = {
  id: ProductHelpCategoryId;
  title: string;
  description: string;
};

export type ProductHelpStep = {
  title: string;
  body: string;
};

export type ProductHelpSection = {
  id: string;
  title: string;
  paragraphs?: string[];
  steps?: ProductHelpStep[];
};

export type ProductHelpAction = {
  label: string;
  href: string;
};

export type ProductHelpArticle = {
  id: string;
  title: string;
  summary: string;
  goal: string;
  category: ProductHelpCategoryId;
  keywords: string[];
  sections: ProductHelpSection[];
  expectedResult: string;
  troubleshooting: ProductHelpStep[];
  relatedArticleIds: string[];
  portalRoutes: string[];
  actions: ProductHelpAction[];
  owner: string;
  lastReviewed: string;
  reviewAfter: string;
  featuredRank?: number;
};

export const productHelpCategories: ProductHelpCategory[] = [
  { id: 'start', title: 'Get started', description: 'Sign in, orient yourself, and move confidently through the Portal.' },
  { id: 'account-access', title: 'Account and access', description: 'Work in the right tenant and understand why available workspaces can change.' },
  { id: 'commerce', title: 'Sell and manage offers', description: 'Create catalog content, make offers available, and follow orders.' },
  { id: 'maps', title: 'Maps', description: 'Choose the right map workspace and manage tenant-owned map assets.' },
  { id: 'automation', title: 'API and automation', description: 'Manage API access and use the authenticated developer console safely.' },
  { id: 'troubleshooting', title: 'Troubleshooting', description: 'Recover when a workspace, tenant, or expected record is missing.' },
];

const governance = {
  owner: 'ConnectPlane Product',
  lastReviewed: '2026-10-29',
  reviewAfter: '2027-01-29',
};

export const productHelpArticles: ProductHelpArticle[] = [
  {
    id: 'getting-started',
    title: 'Get started in ConnectPlane',
    summary: 'Confirm your tenant, find your workspace, and complete your first Portal task.',
    goal: 'Start work in the correct tenant without having to learn the Portal structure first.',
    category: 'start',
    keywords: ['first steps', 'portal basics', 'where do I begin', 'navigation', 'home'],
    sections: [
      {
        id: 'start',
        title: 'Start from Portal home',
        steps: [
          { title: 'Sign in', body: 'Use your organization’s normal sign-in method. ConnectPlane opens the tenants your account can access.' },
          { title: 'Confirm the active tenant', body: 'Check the tenant name in the Portal header before opening or changing data.' },
          { title: 'Choose a workspace', body: 'Use the main navigation for common workspaces, or open command search to jump directly to a page.' },
          { title: 'Keep your context', body: 'If you move between tenants, confirm the header again because data and available workspaces can change.' },
        ],
      },
    ],
    expectedResult: 'You are on the intended tenant and can open the workspace for your task.',
    troubleshooting: [
      { title: 'The tenant is not listed', body: 'Your account may not have membership in that tenant. Confirm the sign-in account and ask a tenant administrator to verify access.' },
      { title: 'A workspace is missing', body: 'Check the active tenant first. Workspace visibility can also depend on product availability and your permissions.' },
    ],
    relatedArticleIds: ['switch-tenant', 'find-a-workspace', 'troubleshoot-access'],
    portalRoutes: ['/dashboard'],
    actions: [{ label: 'Open Portal home', href: '/dashboard' }],
    featuredRank: 1,
    ...governance,
  },
  {
    id: 'switch-tenant',
    title: 'Switch to another tenant',
    summary: 'Change the active tenant and verify that you are working with the intended organization and data.',
    goal: 'Move between authorized tenants without changing or viewing data in the wrong scope.',
    category: 'account-access',
    keywords: ['switch tenant', 'change organization', 'tenant selector', 'wrong tenant', 'scope'],
    sections: [
      {
        id: 'switch',
        title: 'Change tenant context',
        steps: [
          { title: 'Open the tenant selector', body: 'Use the tenant control in the Portal header. On a smaller screen, open the navigation area first.' },
          { title: 'Select a tenant', body: 'Choose from the tenants currently available to your signed-in account.' },
          { title: 'Verify the new context', body: 'Confirm the tenant name in the header before continuing. Navigation and page data refresh for that tenant.' },
        ],
      },
    ],
    expectedResult: 'The Portal header shows the selected tenant and tenant-scoped pages use that context.',
    troubleshooting: [
      { title: 'Only one tenant appears', body: 'The selector only shows tenants your current account can access. A tenant administrator can verify membership.' },
      { title: 'The page looks different after switching', body: 'Available workspaces and records can differ by tenant, product enrollment, and permission.' },
    ],
    relatedArticleIds: ['getting-started', 'find-a-workspace', 'troubleshoot-access'],
    portalRoutes: ['/dashboard'],
    actions: [{ label: 'Open Portal home', href: '/dashboard' }],
    featuredRank: 2,
    ...governance,
  },
  {
    id: 'find-a-workspace',
    title: 'Find a workspace or page',
    summary: 'Use navigation and command search to reach a Portal task without browsing every menu.',
    goal: 'Open the right workspace quickly and understand why it may not be visible.',
    category: 'start',
    keywords: ['find page', 'search portal', 'command menu', 'navigation', 'missing menu'],
    sections: [
      {
        id: 'find',
        title: 'Find where you need to go',
        steps: [
          { title: 'Use the main navigation', body: 'Choose a product area such as Commerce, Maps, Sales, API Gateway, Settings, or Help.' },
          { title: 'Search by task', body: 'Open command search and enter a page name or a question such as “switch tenant” or “API access.”' },
          { title: 'Check the page context', body: 'After navigating, confirm the active tenant and page heading before making changes.' },
        ],
      },
    ],
    expectedResult: 'The intended workspace opens inside the current tenant context.',
    troubleshooting: [
      { title: 'Search finds help but not a page', body: 'Open the help result for the task. The article links to the relevant workspace when one is available.' },
      { title: 'The workspace does not appear', body: 'Confirm tenant context, then check with an administrator whether the tenant has the product and your role has access.' },
    ],
    relatedArticleIds: ['getting-started', 'switch-tenant', 'troubleshoot-access'],
    portalRoutes: ['/dashboard'],
    actions: [{ label: 'Open Portal home', href: '/dashboard' }],
    featuredRank: 3,
    ...governance,
  },
  {
    id: 'manage-catalog',
    title: 'Create and organize a catalog',
    summary: 'Use Commerce Catalogs to organize products and the variants customers can buy.',
    goal: 'Create a clear catalog structure before pricing or publishing offers.',
    category: 'commerce',
    keywords: ['catalog', 'product', 'variant', 'commerce', 'offer setup'],
    sections: [
      {
        id: 'catalog',
        title: 'Build the catalog structure',
        steps: [
          { title: 'Open Catalogs', body: 'In the Portal, open Commerce and choose Catalogs.' },
          { title: 'Create or open a catalog', body: 'Use a name that identifies the offer family or selling purpose for other operators.' },
          { title: 'Add products and variants', body: 'Products describe the offer; variants hold the selectable configurations used by pricing and checkout.' },
          { title: 'Review before publishing', body: 'Confirm names, availability state, and variant details before connecting the catalog to commercial settings.' },
        ],
      },
    ],
    expectedResult: 'The catalog contains an understandable product and variant structure ready for commercial configuration.',
    troubleshooting: [
      { title: 'A product is not sellable yet', body: 'A catalog alone does not make an offer available. Pricing, publication, channel, and eligibility settings may also be required.' },
      { title: 'You cannot edit the catalog', body: 'Confirm the active tenant and ask an administrator to verify your Commerce permissions.' },
    ],
    relatedArticleIds: ['publish-offers', 'review-orders', 'troubleshoot-access'],
    portalRoutes: ['/commerce/catalogs'],
    actions: [{ label: 'Open Catalogs', href: '/commerce/catalogs' }],
    featuredRank: 5,
    ...governance,
  },
  {
    id: 'publish-offers',
    title: 'Make catalog offers available',
    summary: 'Connect a catalog to pricing, an audience, storefront access, and eligibility rules.',
    goal: 'Move a prepared catalog into an intentional selling context without confusing visibility with sellability.',
    category: 'commerce',
    keywords: ['publication', 'pricebook', 'channel', 'eligibility', 'market', 'assignment', 'discount'],
    sections: [
      {
        id: 'sequence',
        title: 'Configure the selling path',
        steps: [
          { title: 'Confirm catalog readiness', body: 'Verify the products and variants that should be offered before configuring commercial rules.' },
          { title: 'Configure pricing', body: 'Use a pricebook for customer-facing amounts. Keep pricing separate from the catalog structure.' },
          { title: 'Create or review the publication', body: 'A publication connects the catalog to its intended audience and default commercial settings.' },
          { title: 'Check channel and eligibility', body: 'Use channels for approved storefront access and eligibility for serviceability or selling constraints.' },
          { title: 'Verify the resolved experience', body: 'Review the intended storefront or sales flow in the same tenant and selling context.' },
        ],
      },
    ],
    expectedResult: 'The intended audience can resolve the correct catalog, pricing, and eligibility outcome through an approved channel.',
    troubleshooting: [
      { title: 'The catalog is visible but cannot be purchased', body: 'Check eligibility and whether the selected market or selling context satisfies the active rules.' },
      { title: 'The wrong price appears', body: 'Review the publication default and any more-specific pricebook assignments for the selling context.' },
    ],
    relatedArticleIds: ['manage-catalog', 'review-orders', 'troubleshoot-access'],
    portalRoutes: ['/commerce/publications', '/commerce/pricebooks', '/commerce/channels', '/commerce/eligibility'],
    actions: [{ label: 'Open Publications', href: '/commerce/publications' }],
    featuredRank: 6,
    ...governance,
  },
  {
    id: 'review-orders',
    title: 'Review quotes and orders',
    summary: 'Find a sales record, confirm its tenant and status, and open the detail needed for the next action.',
    goal: 'Follow a customer transaction without losing tenant or record context.',
    category: 'commerce',
    keywords: ['orders', 'quotes', 'carts', 'sales order', 'customer transaction'],
    sections: [
      {
        id: 'review',
        title: 'Follow a transaction',
        steps: [
          { title: 'Open the relevant registry', body: 'Use Quotes, Carts, or Orders based on the current stage of the customer transaction.' },
          { title: 'Search and filter', body: 'Narrow the registry using the customer or record information available to you.' },
          { title: 'Open the record', body: 'Review status, line items, totals, and recent activity before taking the next available action.' },
          { title: 'Confirm the result', body: 'Return to the registry and verify that the status reflects any completed action.' },
        ],
      },
    ],
    expectedResult: 'You can identify the current transaction state and the next action available to your role.',
    troubleshooting: [
      { title: 'The record is missing', body: 'Confirm the active tenant and remove overly narrow filters. Records from another tenant are not shown.' },
      { title: 'An action is unavailable', body: 'The record state or your permissions may not allow that transition. Review the status before retrying.' },
    ],
    relatedArticleIds: ['manage-catalog', 'publish-offers', 'troubleshoot-access'],
    portalRoutes: ['/commerce/orders', '/commerce/quotes', '/commerce/carts'],
    actions: [{ label: 'Open Orders', href: '/commerce/orders' }],
    ...governance,
  },
  {
    id: 'manage-map-assets',
    title: 'Choose and manage map assets',
    summary: 'Use Sources, Styles, and Custom Maps for the correct part of a tenant map experience.',
    goal: 'Open the right Maps workspace and avoid mixing data sources with presentation or composed experiences.',
    category: 'maps',
    keywords: ['maps', 'source', 'style', 'custom map', 'sprites', 'glyphs', 'sales map'],
    sections: [
      {
        id: 'choose',
        title: 'Choose the right map workspace',
        steps: [
          { title: 'Start with the goal', body: 'Use Sources for data origins, Styles for presentation rules, and Custom Maps for composed tenant experiences.' },
          { title: 'Open the asset', body: 'Select an existing asset before creating a new one so you do not duplicate tenant configuration.' },
          { title: 'Review dependencies', body: 'Before changing or removing an asset, check which styles or custom maps rely on it.' },
          { title: 'Verify the rendered result', body: 'Preview the affected map experience after saving a change.' },
        ],
      },
    ],
    expectedResult: 'The map asset is managed in the appropriate workspace and its dependent experience still renders as intended.',
    troubleshooting: [
      { title: 'An asset is missing', body: 'Confirm the active tenant and check the neighboring Maps workspace in case the item is a source, style, or custom map.' },
      { title: 'A map does not render as expected', body: 'Check the source first, then the style, and finally the custom map composition to isolate the layer with the problem.' },
    ],
    relatedArticleIds: ['find-a-workspace', 'switch-tenant', 'troubleshoot-access'],
    portalRoutes: ['/maps/sources', '/maps/styles', '/maps/custom-maps', '/sales'],
    actions: [{ label: 'Open Map Sources', href: '/maps/sources' }],
    ...governance,
  },
  {
    id: 'use-api-access',
    title: 'Manage API access safely',
    summary: 'Choose between API Access, the authenticated console, and public developer documentation.',
    goal: 'Use the right API surface without placing a credential in browser code, documentation, or chat.',
    category: 'automation',
    keywords: ['api gateway', 'API key', 'developer console', 'automation', 'OpenAPI', 'credentials'],
    sections: [
      {
        id: 'choose',
        title: 'Choose the API surface',
        steps: [
          { title: 'Use API Access for credentials', body: 'Open API Gateway Access in the Portal to manage tenant API access when your role permits it.' },
          { title: 'Use the console for an authenticated browser session', body: 'The Portal developer console is the interactive surface for signed-in users.' },
          { title: 'Use Developer Docs for integration work', body: 'Use the public API guides and read-only reference for stable operations, schemas, errors, and SDK guidance.' },
          { title: 'Keep keys in a secret store', body: 'Never place an API key in source control, URLs, browser code, logs, chat, or an AI prompt.' },
        ],
      },
    ],
    expectedResult: 'You can manage or explore API access through the appropriate authenticated surface while keeping credentials private.',
    troubleshooting: [
      { title: 'API Gateway is not visible', body: 'Confirm the active tenant, product availability, and your permissions with a tenant administrator.' },
      { title: 'A request returns 403', body: 'The credential is valid but does not grant that operation. Review the key scope rather than repeatedly retrying.' },
    ],
    relatedArticleIds: ['troubleshoot-access', 'find-a-workspace'],
    portalRoutes: ['/api-gateway/access', '/api-gateway/console', '/api-access'],
    actions: [
      { label: 'Open API Access', href: '/api-gateway/access' },
      { label: 'Read Developer Docs', href: 'https://docs.connectplane.com/' },
    ],
    featuredRank: 7,
    ...governance,
  },
  {
    id: 'troubleshoot-access',
    title: 'Troubleshoot missing access or data',
    summary: 'Check tenant context, filters, product availability, and permissions in the right order.',
    goal: 'Identify the most likely reason a workspace or record is missing before escalating the issue.',
    category: 'troubleshooting',
    keywords: ['missing data', 'permission denied', 'workspace missing', 'cannot access', 'empty page', 'support'],
    sections: [
      {
        id: 'checks',
        title: 'Run the quick checks',
        steps: [
          { title: 'Confirm the active tenant', body: 'Most Portal data and navigation are tenant-scoped. Verify the tenant shown in the header.' },
          { title: 'Check search and filters', body: 'Clear narrow filters and confirm that you are using the registry that owns the record.' },
          { title: 'Refresh your session once', body: 'If access was recently changed, sign in again so the Portal can load the current tenant and permission state.' },
          { title: 'Check product and role access', body: 'Ask a tenant administrator to verify that the tenant has the workspace and that your role grants the required access.' },
          { title: 'Capture useful context', body: 'If the problem remains, record the tenant name, page, time, safe error text, and request identifier. Do not include credentials.' },
        ],
      },
    ],
    expectedResult: 'You either recover access or have a concise, credential-free description that an administrator or support contact can investigate.',
    troubleshooting: [
      { title: 'The page says permission denied', body: 'Do not keep retrying. Confirm tenant context and ask an administrator to review the permission required for that task.' },
      { title: 'The page shows an unexpected error', body: 'Keep the request identifier and timestamp. They are more useful for investigation than screenshots containing private customer data.' },
    ],
    relatedArticleIds: ['switch-tenant', 'find-a-workspace', 'getting-started'],
    portalRoutes: ['/support', '/settings'],
    actions: [{ label: 'Open Support', href: '/support' }],
    featuredRank: 4,
    ...governance,
  },
];

export const legacyProductHelpAliases: Record<string, string> = {
  'portal-home': 'getting-started',
  'tenant-context': 'switch-tenant',
  'accounts-overview': 'find-a-workspace',
  'account-services': 'find-a-workspace',
  'billing-payments': 'find-a-workspace',
  support: 'troubleshoot-access',
  maps: 'manage-map-assets',
  'sales-map': 'manage-map-assets',
  'api-gateway': 'use-api-access',
  settings: 'troubleshoot-access',
  'commerce-catalogs': 'manage-catalog',
  'commerce-publications': 'publish-offers',
  'commerce-channels': 'publish-offers',
  'commerce-markets': 'publish-offers',
  'commerce-eligibility': 'publish-offers',
  'commerce-reseller_entitlements': 'publish-offers',
  'commerce-pricebooks': 'publish-offers',
  'commerce-assignments': 'publish-offers',
  'commerce-commission_plans': 'publish-offers',
  'commerce-commission_ledger': 'review-orders',
  'commerce-discounts': 'publish-offers',
  'commerce-orders': 'review-orders',
};

const articleById = new Map(productHelpArticles.map(article => [article.id, article]));

export function resolveProductHelpArticleId(articleId: string | null | undefined): string | null {
  const normalized = articleId?.trim();
  if (!normalized) return null;
  if (articleById.has(normalized)) return normalized;
  return legacyProductHelpAliases[normalized] ?? null;
}

export function getProductHelpArticle(articleId: string | null | undefined): ProductHelpArticle | null {
  const resolvedId = resolveProductHelpArticleId(articleId);
  return resolvedId ? articleById.get(resolvedId) ?? null : null;
}

export function getProductHelpCategory(categoryId: ProductHelpCategoryId): ProductHelpCategory {
  const category = productHelpCategories.find(candidate => candidate.id === categoryId);
  if (!category) throw new Error(`Unknown Product Help category: ${categoryId}`);
  return category;
}

export function getProductHelpArticlesByCategory(categoryId: ProductHelpCategoryId): ProductHelpArticle[] {
  return productHelpArticles.filter(article => article.category === categoryId);
}

const stopWords = new Set(['a', 'an', 'and', 'can', 'do', 'for', 'how', 'i', 'in', 'is', 'my', 'of', 'the', 'to', 'where']);

function normalizeSearchValue(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function searchTokens(value: string): string[] {
  return [...new Set(normalizeSearchValue(value).split(' ').filter(token => token.length > 1 && !stopWords.has(token)))];
}

function articleBody(article: ProductHelpArticle): string {
  return article.sections
    .flatMap(section => [
      section.title,
      ...(section.paragraphs ?? []),
      ...(section.steps ?? []).flatMap(step => [step.title, step.body]),
    ])
    .concat(article.expectedResult, ...article.troubleshooting.flatMap(item => [item.title, item.body]))
    .join(' ');
}

export function productHelpSearchTerms(article: ProductHelpArticle): string[] {
  return [article.title, article.summary, article.goal, ...article.keywords, articleBody(article)];
}

export function rankProductHelpArticles(query: string, limit = productHelpArticles.length): ProductHelpArticle[] {
  const phrase = normalizeSearchValue(query);
  const tokens = searchTokens(query);
  if (!phrase || tokens.length === 0) {
    return [...productHelpArticles]
      .sort((a, b) => (a.featuredRank ?? 99) - (b.featuredRank ?? 99) || a.title.localeCompare(b.title))
      .slice(0, limit);
  }

  return productHelpArticles
    .map(article => {
      const title = normalizeSearchValue(article.title);
      const summary = normalizeSearchValue(article.summary);
      const goal = normalizeSearchValue(article.goal);
      const keywords = article.keywords.map(normalizeSearchValue);
      const body = normalizeSearchValue(articleBody(article));
      let score = title === phrase ? 140 : title.includes(phrase) ? 70 : 0;
      score += keywords.some(keyword => keyword === phrase) ? 100 : keywords.some(keyword => keyword.includes(phrase)) ? 55 : 0;
      score += summary.includes(phrase) ? 38 : 0;
      score += goal.includes(phrase) ? 34 : 0;
      for (const token of tokens) {
        if (title.includes(token)) score += 24;
        if (keywords.some(keyword => keyword.includes(token))) score += 18;
        if (summary.includes(token)) score += 9;
        if (goal.includes(token)) score += 10;
        if (body.includes(token)) score += 3;
      }
      return { article, score };
    })
    .filter(result => result.score > 0)
    .sort((a, b) => b.score - a.score || (a.article.featuredRank ?? 99) - (b.article.featuredRank ?? 99) || a.article.title.localeCompare(b.article.title))
    .slice(0, limit)
    .map(result => result.article);
}

export function validateProductHelpCatalog(asOf: string): string[] {
  const issues: string[] = [];
  const ids = new Set<string>();
  const asOfTime = Date.parse(`${asOf}T00:00:00Z`);
  if (Number.isNaN(asOfTime)) return [`Invalid validation date: ${asOf}`];

  for (const article of productHelpArticles) {
    if (ids.has(article.id)) issues.push(`Duplicate article id: ${article.id}`);
    ids.add(article.id);
    if (!article.title.trim() || !article.summary.trim() || !article.goal.trim()) issues.push(`${article.id}: missing required copy`);
    if (!article.owner.trim()) issues.push(`${article.id}: missing owner`);
    if (article.sections.length === 0 || !article.sections.some(section => (section.steps?.length ?? 0) > 0)) issues.push(`${article.id}: missing task steps`);
    if (!article.expectedResult.trim()) issues.push(`${article.id}: missing expected result`);
    if (article.troubleshooting.length === 0) issues.push(`${article.id}: missing troubleshooting`);
    if (Number.isNaN(Date.parse(`${article.lastReviewed}T00:00:00Z`))) issues.push(`${article.id}: invalid lastReviewed`);
    const reviewAfter = Date.parse(`${article.reviewAfter}T23:59:59Z`);
    if (Number.isNaN(reviewAfter)) issues.push(`${article.id}: invalid reviewAfter`);
    else if (reviewAfter < asOfTime) issues.push(`${article.id}: review expired on ${article.reviewAfter}`);
    for (const route of article.portalRoutes) {
      if (!route.startsWith('/')) issues.push(`${article.id}: invalid Portal route ${route}`);
    }
    for (const action of article.actions) {
      if (!(action.href.startsWith('/') || action.href.startsWith('https://'))) issues.push(`${article.id}: invalid action ${action.href}`);
    }
  }

  for (const article of productHelpArticles) {
    for (const relatedId of article.relatedArticleIds) {
      if (!ids.has(relatedId)) issues.push(`${article.id}: unknown related article ${relatedId}`);
    }
  }

  for (const [legacyId, currentId] of Object.entries(legacyProductHelpAliases)) {
    if (ids.has(legacyId)) issues.push(`Legacy alias collides with current article: ${legacyId}`);
    if (!ids.has(currentId)) issues.push(`Legacy alias ${legacyId} has unknown target ${currentId}`);
  }

  return issues;
}

